import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircleOff, RotateCcw } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REASONS = {
  invalid_format: 'Format nomor tidak valid',
  qontak_invalid: 'Ditolak Qontak (nomor tidak valid)',
  undeliverable: 'Gagal terkirim berulang (bukan nomor WA aktif)',
};
const CAN_RESET = ['admin', 'super_admin', 'superadmin', 'kepala_cabang'];

// Delta #72: badge "WA TIDAK AKTIF" + popover alasan & tombol Aktifkan Lagi
export const WaInactiveBadge = ({ customer, compact = false }) => {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!customer?.wa_inactive || hidden) return null;
  const canReset = CAN_RESET.includes((user?.role || '').toLowerCase());
  const tid = `${compact ? 'mobile-' : ''}wa-inactive`;

  const reactivate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/v2/customers/${customer.id}/wa-status/reset`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success(`WA ${customer.name} diaktifkan lagi — pesan berikutnya akan dikirim`);
      setHidden(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal mengaktifkan WA');
    } finally {
      setBusy(false);
    }
  };

  return (
    <span onClick={(e) => e.stopPropagation()} className="inline-flex flex-shrink-0">
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" data-testid={`${tid}-badge-${customer.id}`} title="WhatsApp tidak dikirim ke nomor ini — klik untuk detail">
            <Badge className={`inline-flex items-center gap-1 bg-rose-50 dark:bg-rose-900/30 border border-rose-400 text-rose-700 dark:text-rose-200 font-semibold whitespace-nowrap cursor-pointer hover:bg-rose-100 transition-colors ${compact ? 'text-[9px] py-0.5 px-2' : 'text-[10px] py-0.5 px-1.5'}`}>
              <MessageCircleOff className="w-3 h-3" />
              WA TIDAK AKTIF
            </Badge>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 text-xs space-y-2" data-testid={`${tid}-popover-${customer.id}`}>
          <p className="font-semibold text-sm text-rose-700 dark:text-rose-300">WhatsApp tidak dikirim ke nomor ini</p>
          <p><span className="text-slate-500">Nomor:</span> <span className="font-mono">{customer.phone || '-'}</span></p>
          <p data-testid={`${tid}-reason-${customer.id}`}>
            <span className="text-slate-500">Alasan:</span> {REASONS[customer.wa_inactive_reason] || customer.wa_inactive_reason}
          </p>
          {customer.wa_inactive_note && <p className="text-slate-500 italic">{customer.wa_inactive_note}</p>}
          <p className="text-slate-500">Minta nomor WA baru ke pelanggan lalu ubah di data pelanggan — status otomatis aktif kembali.</p>
          {canReset && (
            <Button size="sm" variant="outline" className="w-full" onClick={reactivate} disabled={busy} data-testid={`${compact ? 'mobile-' : ''}wa-reactivate-btn-${customer.id}`}>
              <RotateCcw className={`w-3.5 h-3.5 mr-1 ${busy ? 'animate-spin' : ''}`} />
              {busy ? 'Memproses...' : 'Aktifkan Lagi (nomor sudah benar)'}
            </Button>
          )}
        </PopoverContent>
      </Popover>
    </span>
  );
};

export default WaInactiveBadge;
